import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NgxParticlesModule } from '@tsparticles/angular';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { Subject, timer } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { VotingService } from './voting.service';
import { AuthUserService } from '../init/auth-user.service';

@Component({
  selector: 'app-voting-no-active-election',
  standalone: true,
  imports: [CommonModule, NgxParticlesModule],
  templateUrl: './voting-no-active-election.component.html',
  styleUrls: ['./voting-no-active-election.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VotingNoActiveElectionComponent implements OnDestroy, OnInit {
  @Output() activeElectionDetected = new EventEmitter<void>();

  particlesId = 'no-active-election-hero-particles';

  private readonly destroy$ = new Subject<void>();

  particlesOptions: ISourceOptions = {
    fullScreen: {
      enable: false,
    },
    background: {
      color: 'transparent',
    },
    fpsLimit: 60,
    detectRetina: true,
    particles: {
      number: {
        value: 110,
        density: {
          enable: true,
          width: 1920,
          height: 520,
        },
      },
      color: {
        value: ['#9fd688', '#dff6d7', '#74b464'],
      },
      links: {
        enable: true,
        distance: 138,
        color: '#c6e9bb',
        opacity: 0.2,
        width: 1,
      },
      move: {
        enable: true,
        speed: 0.55,
        direction: 'none',
        outModes: {
          default: 'out',
        },
      },
      opacity: {
        value: {
          min: 0.16,
          max: 0.34,
        },
      },
      size: {
        value: {
          min: 1,
          max: 3.2,
        },
      },
      shape: {
        type: 'circle',
      },
    },
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: 'grab',
        },
      },
      modes: {
        grab: {
          distance: 120,
          links: {
            opacity: 0.16,
          },
        },
      },
    },
  };

  constructor(private readonly votingService: VotingService,
              private readonly auth: AuthUserService) { }

  particlesInit = async (engine: Engine): Promise<void> => {
    await loadSlim(engine);
  };

  ngOnInit(): void {
    timer(10000, 10000).pipe(
      switchMap(() => this.votingService.checkElectionStatus(this.auth.getCurrentUser().electionId)),
      filter((status) => status.status === 'active'),
      take(1),
      takeUntil(this.destroy$),
    ).subscribe((status) => {
      this.activeElectionDetected.emit();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
